// =============================================================================
// AffiliatePerformance — tenant-admin subpage that surfaces affiliate-link
// click telemetry: which partners drive the most clicks, which bulletins
// are converting, and how the trend is moving over time.
//
// Data sources (all Platform DB):
//   - public.affiliate_click_events  (raw click log; one row per click)
//   - get_affiliate_clicks_by_day    (daily series for trend chart)
//   - get_affiliate_clicks_by_bulletin (top bulletins for a window)
//   - fetchAffiliateClickStats        (existing, gives by_partner aggregation)
//
// Auth: page is rendered inside TenantManage which already requires the
// caller to be a member/admin of the tenant. The Platform DB RLS on
// affiliate_click_events restricts SELECT accordingly.
// =============================================================================

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from 'recharts';
import {
  Link2, ExternalLink, TrendingUp, Trophy, AlertCircle, ArrowRight,
} from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import {
  fetchAffiliateClickStats,
  fetchAffiliateClicksByDay,
  fetchAffiliateClicksByBulletin,
  type AffiliateClickByDay,
  type AffiliateClickByBulletin,
} from '@/lib/api';
import { formatCompact, formatRelative } from '@/utils/helpers';
import { PARTNER_CATEGORIES } from './Affiliates';
import type { Tenant, AffiliateClickStats } from '@/types';

// ── Window selection (mirrors Dashboard) ───────────────────────────────────

type PresetDays = 7 | 14 | 30 | 90 | 365;

interface AffiliatePerformancePageProps {
  tenant: Tenant;
}

// Build a flat partner_key → partner_name lookup from PARTNER_CATEGORIES.
function buildPartnerNameMap(): Map<string, string> {
  const map = new Map<string, string>();
  for (const cat of PARTNER_CATEGORIES) {
    for (const p of cat.partners) map.set(p.key, p.name);
  }
  return map;
}

function isoDate(d: Date) { return d.toISOString().split('T')[0]; }

export function AffiliatePerformancePage({ tenant }: AffiliatePerformancePageProps) {
  const [windowDays, setWindowDays] = useState<PresetDays>(30);
  const [stats, setStats]     = useState<AffiliateClickStats | null>(null);
  const [byDay, setByDay]     = useState<AffiliateClickByDay[]>([]);
  const [byBulletin, setByBulletin] = useState<AffiliateClickByBulletin[]>([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    const today = new Date();
    return {
      from: isoDate(new Date(today.getTime() - (windowDays - 1) * 86400000)),
      to:   isoDate(today),
    };
  }, [windowDays]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchAffiliateClickStats(tenant.id, windowDays),
      fetchAffiliateClicksByDay(tenant.id, range.from, range.to),
      fetchAffiliateClicksByBulletin(tenant.id, range.from, range.to, 10),
    ])
      .then(([s, d, b]) => { setStats(s); setByDay(d); setByBulletin(b); })
      .catch(() => { setStats(null); setByDay([]); setByBulletin([]); })
      .finally(() => setLoading(false));
  }, [tenant.id, windowDays, range.from, range.to]);

  const partnerNameMap = useMemo(buildPartnerNameMap, []);
  const prettyPartner = (key: string): string => partnerNameMap.get(key) || key.replace(/_/g, '.');

  // Pad daily series so the line chart always shows N data points.
  const paddedSeries = useMemo(() => {
    const out: { day: string; clicks: number }[] = [];
    const byDate = new Map(byDay.map(r => [r.day, r.clicks]));
    const fromMs = new Date(range.from).getTime();
    for (let i = 0; i < windowDays; i++) {
      const d = isoDate(new Date(fromMs + i * 86400000));
      out.push({ day: d.slice(5), clicks: byDate.get(d) ?? 0 });
    }
    return out;
  }, [byDay, range.from, windowDays]);

  const partnerBars = useMemo(() => {
    const rows = stats?.by_partner ?? [];
    return rows
      .map((p: { partner_key: string; partner_name: string; clicks: number }) => ({
        ...p,
        partner_label: prettyPartner(p.partner_key),
      }))
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 12);
  }, [stats, prettyPartner]);

  return (
    <div className="space-y-6">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-surface-900 flex items-center gap-2">
            <Link2 size={18} className="text-brand-500" />
            Affiliate Performance
          </h1>
          <p className="text-sm text-surface-500 mt-1">
            Click telemetry from bulletin booking links across all partners.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <WindowToggle value={windowDays} onChange={setWindowDays} />
          <Link
            to={`/admin/tenants/${tenant.id}/manage/affiliates`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-700 transition-colors"
          >
            Manage IDs <ExternalLink size={11} />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner size="lg" /></div>
      ) : !stats || stats.total_clicks === 0 ? (
        <EmptyState tenant={tenant} />
      ) : (
        <>
          {/* ── KPI strip ─────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCell label="Total clicks" value={formatCompact(stats.total_clicks)} sub={`Last ${windowDays}d`} />
            <KpiCell label="Today" value={formatCompact(stats.clicks_today)} />
            <KpiCell label="This week" value={formatCompact(stats.clicks_this_week)} />
            <KpiCell
              label="Top partner"
              value={partnerBars[0] ? prettyPartner(partnerBars[0].partner_key) : '—'}
              sub={partnerBars[0] ? `${partnerBars[0].clicks} clicks` : undefined}
            />
          </div>

          {/* ── Trend ─────────────────────────────────────────────────── */}
          <div className="glass-panel p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-surface-900 flex items-center gap-2">
                <TrendingUp size={14} className="text-brand-500" /> Click trend · {windowDays}d
              </h3>
            </div>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={paddedSeries} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="rgb(var(--surface-200))" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'rgb(var(--surface-500))' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: 'rgb(var(--surface-500))' }} allowDecimals={false} width={36} />
                  <Tooltip
                    contentStyle={{ background: 'rgb(var(--surface-0))', border: '1px solid rgb(var(--surface-300))', borderRadius: 8, fontSize: 12 }}
                  />
                  <Line type="monotone" dataKey="clicks" stroke="rgb(42, 179, 177)" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── By partner ─────────────────────────────────────────────── */}
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-4">Clicks by partner</h3>
            {partnerBars.length === 0 ? (
              <div className="text-sm text-surface-500 py-6 text-center">No clicks recorded yet for this window.</div>
            ) : (
              <div style={{ height: Math.max(160, partnerBars.length * 28) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={partnerBars} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 8 }}>
                    <CartesianGrid stroke="rgb(var(--surface-200))" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: 'rgb(var(--surface-500))' }} allowDecimals={false} />
                    <YAxis type="category" dataKey="partner_label" width={130}
                      tick={{ fontSize: 11, fill: 'rgb(var(--surface-700))' }} />
                    <Tooltip
                      contentStyle={{ background: 'rgb(var(--surface-0))', border: '1px solid rgb(var(--surface-300))', borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="clicks" fill="rgb(42, 179, 177)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* ── Top bulletins ──────────────────────────────────────────── */}
          <div className="glass-panel p-5">
            <h3 className="text-sm font-semibold text-surface-900 mb-4 flex items-center gap-2">
              <Trophy size={14} className="text-amber-500" /> Top performing bulletins
            </h3>
            {byBulletin.length === 0 ? (
              <div className="text-sm text-surface-500 py-6 text-center">
                No bulletin click data yet — once users click partner links on bulletins they'll show up here.
              </div>
            ) : (
              <div className="space-y-2">
                {byBulletin.map((b, i) => (
                  <div key={`${b.source_id}-${b.partner_key}`} className="flex items-center gap-3 py-1.5 border-b border-overlay/[0.04] last:border-0">
                    <span className="text-xs font-bold text-surface-400 w-5 text-right">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-surface-900 truncate">{b.bulletin_title}</div>
                      <div className="text-[11px] text-surface-500 flex items-center gap-2">
                        <span>{prettyPartner(b.partner_key)}</span>
                        <span>·</span>
                        <span>last click {formatRelative(b.last_click)}</span>
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-surface-900">{b.clicks}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function KpiCell({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  return (
    <div className="glass-panel p-4">
      <div className="text-[10px] uppercase tracking-wider text-surface-500 mb-1">{label}</div>
      <div className="text-lg font-bold text-surface-900 truncate">{value}</div>
      {sub && <div className="text-[10px] text-surface-400 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function WindowToggle({ value, onChange }: {
  value: PresetDays; onChange: (n: PresetDays) => void;
}) {
  const presets: PresetDays[] = [7, 14, 30, 90, 365];
  const labels: Record<PresetDays, string> = { 7: '7d', 14: '14d', 30: '30d', 90: '90d', 365: '1y' };
  return (
    <div className="inline-flex items-center rounded-lg bg-surface-100 p-0.5">
      {presets.map(opt => (
        <button
          key={opt}
          onClick={() => onChange(opt)}
          className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
            value === opt ? 'bg-surface-0 text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'
          }`}
        >
          {labels[opt]}
        </button>
      ))}
    </div>
  );
}

function EmptyState({ tenant }: { tenant: Tenant }) {
  return (
    <div className="glass-panel p-10 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-surface-100 text-surface-400 mb-4">
        <AlertCircle size={20} />
      </div>
      <h3 className="text-base font-semibold text-surface-900 mb-2">No affiliate clicks yet</h3>
      <p className="text-sm text-surface-500 max-w-md mx-auto mb-4">
        Once users click partner booking links from your bulletins, the clicks will be tracked here
        — broken down by partner, bulletin, and over time.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <Link
          to={`/admin/tenants/${tenant.id}/manage/affiliates`}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-brand-500 hover:bg-brand-600 text-white transition-colors"
        >
          Set partner IDs <ArrowRight size={11} />
        </Link>
        <Link
          to={`/admin/tenants/${tenant.id}/manage/bulletins`}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-700 transition-colors"
        >
          Add a bulletin with a booking link
        </Link>
      </div>
    </div>
  );
}
